import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, User, Mail, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Settings() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailForTranscripts, setEmailForTranscripts] = useState('');
  const [sendConversationRecap, setSendConversationRecap] = useState(false);
  const [recapEmail, setRecapEmail] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [userRes, prefsRes] = await Promise.all([
        axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/auth/email-preferences`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setUser(userRes.data);
      setEmailForTranscripts(userRes.data.email);
      setSendConversationRecap(prefsRes.data.send_recap || false);
      setRecapEmail(prefsRes.data.email || userRes.data.email);
    } catch (error) {
      toast.error('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/auth/email-preferences`,
        {
          send_recap: sendConversationRecap,
          email: recapEmail || user.email
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Email preferences saved successfully!');
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  if (loading) return <div className="p-8" data-testid="loading">Loading...</div>;

  return (
    <div className="min-h-screen p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
          <h1 className="text-4xl font-bold" style={{ fontFamily: 'Space Grotesk' }} data-testid="settings-title">
            Settings
          </h1>
          <p className="text-gray-600 mt-2">Manage your account and preferences</p>
        </div>

        {/* Profile Information */}
        <Card className="glass mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Profile Information
            </CardTitle>
            <CardDescription>Your account details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Username</Label>
              <Input value={user?.username || ''} disabled data-testid="username-display" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={user?.email || ''} disabled data-testid="email-display" />
            </div>
            <div className="space-y-2">
              <Label>Account Created</Label>
              <Input 
                value={user?.created_at ? new Date(user.created_at).toLocaleDateString() : ''} 
                disabled 
              />
            </div>
          </CardContent>
        </Card>

        {/* Email Preferences */}
        <Card className="glass mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Email Preferences
            </CardTitle>
            <CardDescription>Email address for conversation transcripts</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="send-recap"
                  checked={sendConversationRecap}
                  onChange={(e) => setSendConversationRecap(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  data-testid="send-recap-checkbox"
                />
                <Label htmlFor="send-recap" className="cursor-pointer">
                  Send conversation recap emails
                </Label>
              </div>
              <p className="text-sm text-gray-500 ml-6">
                Receive a summary of each conversation when it ends, so you can review key details at a glance
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="recap-email">Email for Conversation Recaps</Label>
              <Input
                id="recap-email"
                type="email"
                placeholder="your@email.com"
                value={recapEmail}
                onChange={(e) => setRecapEmail(e.target.value)}
                disabled={!sendConversationRecap}
                className={!sendConversationRecap ? 'bg-gray-100' : ''}
                data-testid="recap-email-input"
              />
              <p className="text-sm text-gray-500">
                Conversation summaries will be sent to this email address
              </p>
            </div>
            
            <Button onClick={handleSaveSettings} data-testid="save-settings-button">
              <Save className="w-4 h-4 mr-2" />
              Save Settings
            </Button>
          </CardContent>
        </Card>

        {/* Account Stats */}
        <Card className="glass">
          <CardHeader>
            <CardTitle>Account Statistics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="border rounded-lg p-4">
                <div className="text-sm text-gray-500">Total Clients</div>
                <div className="text-2xl font-bold mt-1">-</div>
              </div>
              <div className="border rounded-lg p-4">
                <div className="text-sm text-gray-500">Total Conversations</div>
                <div className="text-2xl font-bold mt-1">-</div>
              </div>
              <div className="border rounded-lg p-4">
                <div className="text-sm text-gray-500">Knowledge Entries</div>
                <div className="text-2xl font-bold mt-1">-</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
